import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Save,
  X,
  Info,
  Settings,
  DollarSign,
  PiggyBank,
  Plus,
  Trash2
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const EventForm = ({ event = null, onClose = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!event;

  const [formData, setFormData] = useState({
    name: event?.name || '',
    description: event?.description || '',
    start_date: event?.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
    end_date: event?.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
    max_steps: event?.max_steps || 13,
    commission_rate: event?.commission_rate || 50,
    status: event?.status || 'active'
  });

  const [errors, setErrors] = useState({});

  const createEventMutation = useMutation(
    (data) => api.post('/events', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('admin-dashboard');
        toast.success('Evento creado exitosamente');
        if (onClose) {
          onClose();
        } else {
          navigate('/admin/events');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al crear evento');
      }
    }
  );

  const updateEventMutation = useMutation(
    ({ id, data }) => api.put(`/events/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries(['event', event.id]);
        queryClient.invalidateQueries('admin-dashboard');
        toast.success('Evento actualizado exitosamente');
        if (onClose) {
          onClose();
        } else {
          navigate('/admin/events');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar evento');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validaciones
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }

    if (formData.max_steps < 1 || formData.max_steps > 20) {
      newErrors.max_steps = 'El número de pasos debe estar entre 1 y 20';
    }

    if (formData.commission_rate < 0 || formData.commission_rate > 100) {
      newErrors.commission_rate = 'La comisión debe estar entre 0% y 100%';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = {
      ...formData,
      max_steps: parseInt(formData.max_steps),
      commission_rate: parseFloat(formData.commission_rate)
    };

    if (isEditing) {
      updateEventMutation.mutate({ id: event.id, data: submitData });
    } else {
      createEventMutation.mutate(submitData);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/admin/events');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? 'Editar Evento' : 'Crear Nuevo Evento'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEditing
              ? 'Modifica la configuración del evento existente'
              : 'Configura un nuevo evento de apuestas'
            }
          </p>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Información Básica */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-primary rounded-lg">
              <Info className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Información Básica
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre del evento *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className={`w-full px-4 py-2.5 bg-white dark:bg-dark-800 border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'} rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                placeholder="Ej: TCOL 2025 (Prueba 2)"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="3"
                placeholder="Descripción opcional del evento..."
              />
            </div>
          </div>
        </motion.div>

        {/* Programación */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Programación
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de inicio
              </label>
              <input
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de finalización
              </label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </motion.div>

        {/* Configuración */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Configuración del Evento
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Número máximo de pasos *
              </label>
              <input
                type="number"
                value={formData.max_steps}
                onChange={(e) => setFormData({...formData, max_steps: e.target.value})}
                className={`w-full px-4 py-2.5 bg-white dark:bg-dark-800 border ${errors.max_steps ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'} rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                min="1"
                max="20"
              />
              {errors.max_steps && (
                <p className="text-red-500 text-sm mt-1">{errors.max_steps}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Entre 1 y 20 pasos</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4" />
                  <span>Comisión (%) *</span>
                </div>
              </label>
              <input
                type="number"
                value={formData.commission_rate}
                onChange={(e) => setFormData({...formData, commission_rate: e.target.value})}
                className={`w-full px-4 py-2.5 bg-white dark:bg-dark-800 border ${errors.commission_rate ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'} rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                min="0"
                max="100"
                step="0.1"
              />
              {errors.commission_rate && (
                <p className="text-red-500 text-sm mt-1">{errors.commission_rate}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Comisión sobre las ganancias netas</p>
            </div>
          </div>
        </motion.div>

        {/* Pozos del Evento (solo al editar, requiere ID del evento) */}
        {isEditing && <EventPotsSection eventId={event.id} />}

        {/* Botones */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
            disabled={createEventMutation.isLoading || updateEventMutation.isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={createEventMutation.isLoading || updateEventMutation.isLoading}
          >
            {createEventMutation.isLoading || updateEventMutation.isLoading ? (
              <>
                <div className="spinner w-4 h-4 mr-2"></div>
                {isEditing ? 'Actualizando...' : 'Creando...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Actualizar Evento' : 'Crear Evento'}
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

// Sección de pozos asociados al evento (cada pozo descuenta su % en cada empate)
const EventPotsSection = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [selectedPotId, setSelectedPotId] = useState('');

  const { data: assocData } = useQuery(
    ['event-pots', eventId],
    () => api.get(`/events/${eventId}/pots`).then(res => res.data.pots)
  );
  const associated = assocData || [];

  const { data: allPotsData } = useQuery(
    ['pots'],
    () => api.get('/pots?limit=100').then(res => res.data.pots)
  );
  const allPots = allPotsData || [];

  const associatedIds = new Set(associated.map(p => p.id));
  const available = allPots.filter(p => !associatedIds.has(p.id));

  const addMutation = useMutation(
    (potId) => api.post(`/events/${eventId}/pots`, { potId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event-pots', eventId]);
        setSelectedPotId('');
        toast.success('Pozo asociado al evento');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Error al asociar el pozo')
    }
  );

  const removeMutation = useMutation(
    (potId) => api.delete(`/events/${eventId}/pots/${potId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event-pots', eventId]);
        toast.success('Pozo desasociado');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Error al desasociar el pozo')
    }
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="card p-6"
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg">
          <PiggyBank className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Pozos del Evento
        </h3>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Cada pozo asociado descuenta su porcentaje de la ganancia (profit) en cada empate, adicional a la comisión de la plataforma.
      </p>

      {associated.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">No hay pozos asociados a este evento.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {associated.map(p => (
            <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-dark-600">
              <div className="flex items-center space-x-3">
                <PiggyBank className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                <span className="text-xs text-gray-500">({Number(p.commission_rate)}%)</span>
                {!p.is_active && <span className="text-xs text-red-500">inactivo</span>}
              </div>
              <button
                type="button"
                onClick={() => removeMutation.mutate(p.id)}
                className="text-red-600 hover:text-red-800 dark:text-red-400"
                title="Quitar pozo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center space-x-2">
        <select
          value={selectedPotId}
          onChange={(e) => setSelectedPotId(e.target.value)}
          className="flex-1 px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100"
        >
          <option value="">Seleccionar pozo a asociar...</option>
          {available.map(p => (
            <option key={p.id} value={p.id}>{p.name} ({Number(p.commission_rate)}%)</option>
          ))}
        </select>
        <button
          type="button"
          disabled={!selectedPotId || addMutation.isLoading}
          onClick={() => addMutation.mutate(parseInt(selectedPotId))}
          className="inline-flex items-center px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4 mr-1" /> Asociar
        </button>
      </div>
    </motion.div>
  );
};

export default EventForm;
